import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Save, X, Loader2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import MaterialTypeSelector from '../components/MaterialCreation/MaterialTypeSelector';
import ViewSelector from '../components/MaterialCreation/ViewSelector';
import GeneralDataView from '../components/MaterialCreation/views/GeneralDataView';
import BasicDataView from '../components/MaterialCreation/views/BasicDataView';
import SalesView from '../components/MaterialCreation/views/SalesView';
import MRPView from '../components/MaterialCreation/views/MRPView';
import ForecastingView from '../components/MaterialCreation/views/ForecastingView';
import ProductionResourcesToolsView from '../components/MaterialCreation/views/ProductionResourcesToolsView';
import PlantDataStorageView from '../components/MaterialCreation/views/PlantDataStorageView';
import AccountingView from '../components/MaterialCreation/views/AccountingView';
import { useSelectedPlants } from '../hooks/useSelectedPlants';
import DynamicFieldsSection from '../components/custom-fields/DynamicFieldsSection';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MaterialEdit = () => {
  const navigate = useNavigate();
  const { materialNumber } = useParams();
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(3); // Start at step 3 (data entry) since material already exists // 1: Select Type, 2: Select Views, 3: Fill Data (removed Sales Org selection step)
  const [materialTypeInfo, setMaterialTypeInfo] = useState(null);
  const [selectedViews, setSelectedViews] = useState({
    general_data: true,  // Always selected
    basic_data: true,    // Always selected
  });
  const [activeTab, setActiveTab] = useState('general_data');
  const [formData, setFormData] = useState({
    general_data: {},
    basic_data: {},
    sales_data: [],  // Unified sales data array
    mrp: {},
    forecasting: {},
    plant_data_storage: [],  // Array for multiple plants
    production_resources_tools: {},
    accounting: {},
    custom_data: {}, // Frappe-style user-defined fields
  });
  
  // Track selected plants across all views for consistency (derived via hook)
  const selectedPlants = useSelectedPlants(formData);

  // Load existing material data
  useEffect(() => {
    if (materialNumber) {
      loadMaterial();
    }
  // eslint-disable-next-line
  }, [materialNumber]);

  const loadMaterial = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/materials/${materialNumber}`);
      const material = response.data;
      
      // Load material type info
      const typeResponse = await axios.get(`${BACKEND_URL}/api/material-types/${material.material_type}`);
      setMaterialTypeInfo({
        materialType: typeResponse.data,
        materialNumber: material.material_number
      });
      
      // Populate formData from loaded material
      const loadedFormData = {
        general_data: {
          industry_sector: material.industry_sector || ''
        },
        basic_data: {
          description: material.description || '',
          base_uom: material.base_uom || '',
          material_group: material.material_group || '',
          weight_unit: material.weight_unit || '',
          gen_item_cater: material.gen_item_cater || '',
          division: material.division || ''
        },
        sales_data: material.sales_data || [],
        mrp: material.mrp || {},
        forecasting: material.forecasting || {},
        // Convert plant_data_storage from nested object to array format for editing
        // Nested format: { "plant_1000": { plant_id: "1000", storage_location: "SL002" } }
        // Array format: [{ plant_id: "1000", storage_location: "SL002" }]
        plant_data_storage: material.plant_data_storage && typeof material.plant_data_storage === 'object' ? 
          (Array.isArray(material.plant_data_storage) ? 
            material.plant_data_storage : 
            // Convert object to array and map field names to match form expectations
            Object.values(material.plant_data_storage).map(plantData => {
              // Map database field names to form field names
              // Database has: storage_location, Form expects: storage_location_id
              return {
                plant_id: plantData.plant_id,
                purchasing_group: plantData.purchasing_group || '',
                storage_location_id: plantData.storage_location || '',
                issue_storage_location: plantData.issue_storage_location || '',
                unit_of_issue: plantData.unit_of_issue || '',
                batch_management: plantData.batch_management || false,
                negative_stocks_in_plant: plantData.negative_stocks_in_plant || false,
                // Include any other fields that might exist
                cost_center: plantData.cost_center || '',
                mrp_controller: plantData.mrp_controller || '',
                abc_indicator: plantData.abc_indicator || '',
                reorder_point: plantData.reorder_point || null,
                safety_stock: plantData.safety_stock || null,
                max_stock_level: plantData.max_stock_level || null
              };
            })
          ) : [],
        production_resources_tools: material.production_resources_tools || {},
        accounting: material.accounting || {},
        custom_data: material.custom_data || {},
      };
      
      setFormData(loadedFormData);
      
      // Set selected views based on what data exists
      const views = {
        general_data: true,
        basic_data: true,
        sales: material.sales_data && material.sales_data.length > 0,
        mrp: material.mrp && Object.keys(material.mrp).length > 0,
        forecasting: material.forecasting && Object.keys(material.forecasting).length > 0,
        plant_data_storage: material.plant_data_storage && Object.keys(material.plant_data_storage).length > 0,
        production_resources_tools: material.production_resources_tools && Object.keys(material.production_resources_tools).length > 0,
        accounting: material.accounting && Object.keys(material.accounting).length > 0
      };
      setSelectedViews(views);
      setActiveTab('general_data');
      
    } catch (error) {
      console.error('Error loading material:', error);
      toast.error('Failed to load material');
      navigate('/materials');
    } finally {
      setLoading(false);
    }
  };

  // Extract and track plants from all views to enforce consistency
  // (handled by useSelectedPlants hook above)

  const handleMaterialTypeSelected = (info) => {
    setMaterialTypeInfo(info);
    
    // Initialize selected views based on enabled_views
    // General Data and Basic Data are always selected (mandatory)
    // Views enabled in material type are pre-selected
    // All other views are available but not pre-selected
    const enabledViews = info.materialType.enabled_views || {};
    const initialSelectedViews = {
      general_data: true,  // Always mandatory
      basic_data: true,    // Always mandatory
    };
    
    // Pre-select views that are enabled in the material type
    Object.keys(enabledViews).forEach(key => {
      if (enabledViews[key] === true) {
        initialSelectedViews[key] = true;
      }
    });
    
    setSelectedViews(initialSelectedViews);
    setStep(2);
  };

  const handleViewToggle = (viewKey, checked) => {
    // Don't allow toggling mandatory views
    if (viewKey === 'general_data' || viewKey === 'basic_data') {
      return;
    }

    setSelectedViews(prev => ({
      ...prev,
      [viewKey]: checked
    }));
  };

  const handleContinueToDataEntry = () => {
    setStep(3); // Go directly to data entry (no Step 3 for sales org selection)
    setActiveTab('general_data');
  };

  const handleUpdate = async () => {
    try {
      // Validate required fields
      if (!formData.general_data?.industry_sector) {
        toast.error('Industry Sector is required');
        setActiveTab('general_data');
        return;
      }

      if (!formData.basic_data?.description) {
        toast.error('Material Description is required');
        setActiveTab('basic_data');
        return;
      }

      if (!formData.basic_data?.base_uom) {
        toast.error('Base Unit of Measure is required');
        setActiveTab('basic_data');
        return;
      }

      if (!formData.basic_data?.material_group) {
        toast.error('Material Group is required');
        setActiveTab('basic_data');
        return;
      }

      // Prepare material data for UPDATE (material number already exists)
      const materialData = {
        // General Data
        material_number: materialNumber, // Use existing material number
        industry_sector: formData.general_data.industry_sector,
        material_type: materialTypeInfo.materialType.material_type_id,
        
        // Basic Data
        description: formData.basic_data.description,
        base_uom: formData.basic_data.base_uom,
        material_group: formData.basic_data.material_group,
        weight_unit: formData.basic_data.weight_unit || null,
        gen_item_cater: formData.basic_data.gen_item_cater || null,
        division: formData.basic_data.division || null,
        
        // Sales Data (if selected and has data)
        sales_data: selectedViews.sales && formData.sales_data?.length > 0 ? formData.sales_data : undefined,
        
        // MRP Data (if selected and has data)
        mrp: selectedViews.mrp && formData.mrp && Object.keys(formData.mrp).length > 0 ? formData.mrp : undefined,
        
        // Forecasting Data (if selected and has data)
        forecasting: selectedViews.forecasting && formData.forecasting && Object.keys(formData.forecasting).length > 0 ? formData.forecasting : undefined,
        
        // Production Resources/Tools Data (if selected and has data)
        production_resources_tools: selectedViews.production_resources_tools && formData.production_resources_tools && Object.keys(formData.production_resources_tools).length > 0 ? formData.production_resources_tools : undefined,
        
        // Plant Data/Storage (if selected and has data)
        // Convert array to nested dictionary format for backend
        plant_data_storage: selectedViews.plant_data_storage && formData.plant_data_storage && Array.isArray(formData.plant_data_storage) && formData.plant_data_storage.length > 0 
          ? formData.plant_data_storage.reduce((acc, plant) => {
              if (plant.plant_id) {
                const key = `plant_${plant.plant_id}`;
                acc[key] = {
                  plant_id: plant.plant_id,
                  storage_location: plant.storage_location_id || null,
                  period_ind_shelf_life: plant.period_ind_shelf_life || null
                };
              }
              return acc;
            }, {})
          : undefined,
        
        // Accounting Data (if selected and has data)
        accounting: selectedViews.accounting && formData.accounting && Object.keys(formData.accounting).length > 0 ? formData.accounting : undefined,
        
        // Status
        status: 'ACTIVE',

        // Frappe-style user-defined fields
        custom_data: formData.custom_data && Object.keys(formData.custom_data).length > 0 ? formData.custom_data : undefined,
      };

      // Save material
      const response = await axios.put(`${BACKEND_URL}/api/materials/${materialNumber}`, materialData);
      
      toast.success(`Material ${response.data.material_number} updated successfully!`);
      
      // Redirect to view materials page after a short delay
      setTimeout(() => {
        navigate('/view-materials');
      }, 2000);
      
    } catch (error) {
      console.error('Error saving material:', error);
      if (error.response?.data?.detail) {
        toast.error(`Error: ${error.response.data.detail}`);
      } else {
        toast.error('Failed to update material');
      }
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All unsaved changes will be lost.')) {
      navigate(`/materials/view/${materialNumber}`);
    }
  };

  const renderTabNavigation = () => {
    const tabs = [
      { key: 'general_data', label: 'General Data', mandatory: true },
      { key: 'basic_data', label: 'Basic Data', mandatory: true },
      { key: 'sales', label: 'Sales' },
      { key: 'mrp', label: 'MRP' },
      { key: 'forecasting', label: 'Forecasting' },
      { key: 'plant_data_storage', label: 'Plant Data / Storage' },
      { key: 'production_resources_tools', label: 'Production Resources / Tools' },
      { key: 'accounting', label: 'Accounting' },
      { key: 'custom_fields', label: 'Custom Fields', alwaysShow: true },
    ];

    // Show mandatory + user-selected tabs + always-shown tabs (e.g. custom fields)
    const visibleTabs = tabs.filter(tab => tab.alwaysShow || selectedViews[tab.key]);

    return (
      <div className="border-b border-gray-200 mb-6 overflow-x-auto">
        <div className="flex space-x-1">
          {visibleTabs.map(tab => {
            const isActive = activeTab === tab.key;

            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`
                  px-4 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors
                  ${isActive 
                    ? 'border-orange-600 text-orange-600 bg-orange-50' 
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50'
                  }
                `}
              >
                {tab.label}
                {tab.mandatory && <span className="ml-1 text-xs text-orange-600">*</span>}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general_data':
        return (
          <GeneralDataView
            formData={formData}
            setFormData={setFormData}
            materialTypeInfo={materialTypeInfo}
          />
        );
      
      case 'basic_data':
        return (
          <BasicDataView
            formData={formData}
            setFormData={setFormData}
          />
        );

      case 'sales':
        return (
          <SalesView
            formData={formData}
            setFormData={setFormData}
          />
        );

      case 'mrp':
        return (
          <MRPView
            formData={formData}
            setFormData={setFormData}
            selectedPlants={selectedPlants}
          />
        );

      case 'forecasting':
        return (
          <ForecastingView
            formData={formData}
            setFormData={setFormData}
            selectedPlants={selectedPlants}
          />
        );

      case 'production_resources_tools':
        return (
          <ProductionResourcesToolsView
            formData={formData}
            setFormData={setFormData}
            selectedPlants={selectedPlants}
          />
        );

      case 'plant_data_storage':
        return (
          <PlantDataStorageView
            formData={formData}
            setFormData={setFormData}
            selectedPlants={selectedPlants}
          />
        );

      case 'accounting':
        return (
          <AccountingView
            formData={formData}
            setFormData={setFormData}
            selectedPlants={selectedPlants}
          />
        );

      case 'custom_fields':
        return (
          <DynamicFieldsSection
            doctype="material"
            values={formData.custom_data || {}}
            onChange={(next) => setFormData({ ...formData, custom_data: next })}
            title="Material Custom Fields"
          />
        );

      default:
        return (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Coming Soon</h3>
            <p className="text-sm text-gray-600 max-w-md">
              This view section will be implemented in future phases. 
              For now, focus on completing the General Data and Basic Data sections.
            </p>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
          <span className="ml-3 text-lg">Loading material...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Edit Material</h1>
              <p className="text-sm text-gray-600 mt-1">
                Follow the steps to edit existing material master record
              </p>
            </div>
            {step === 3 && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  className="flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdate}
                  className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700"
                >
                  <Save className="w-4 h-4" />
                  Update Material
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Progress Indicator */}
        <div className="mb-8 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between max-w-3xl mx-auto">
            {[
              { num: 1, label: 'Select Material Type' },
              { num: 2, label: 'Select Views' },
              { num: 3, label: 'Enter Data' }
            ].map(({ num, label }, idx) => (
              <React.Fragment key={num}>
                <div className="flex flex-col items-center flex-1">
                  <div className={`
                    flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all
                    ${step >= num 
                      ? 'bg-orange-600 border-orange-600 text-white shadow-md' 
                      : 'bg-white border-gray-300 text-gray-500'
                    }
                    font-semibold text-lg
                  `}>
                    {num}
                  </div>
                  <div className={`mt-3 text-sm font-medium text-center ${
                    step >= num ? 'text-orange-700' : 'text-gray-500'
                  }`}>
                    {label}
                  </div>
                </div>
                {idx < 2 && (
                  <div className={`
                    flex-1 h-0.5 mx-4 transition-all
                    ${step > num ? 'bg-orange-600' : 'bg-gray-300'}
                  `} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="pb-8">
          {step === 1 && (
            <MaterialTypeSelector onMaterialTypeSelected={handleMaterialTypeSelected} />
          )}

          {step === 2 && (
            <div className="space-y-6">
              <ViewSelector
                enabledViews={materialTypeInfo?.materialType?.enabled_views || {}}
                selectedViews={selectedViews}
                onViewToggle={handleViewToggle}
              />
              <div className="flex justify-end">
                <Button
                  onClick={handleContinueToDataEntry}
                  className="bg-orange-600 hover:bg-orange-700 px-6 py-2"
                >
                  Continue
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              {/* Tab Navigation */}
              {renderTabNavigation()}

              {/* Tab Content */}
              <div className="p-6">
                {renderTabContent()}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default MaterialEdit;
